import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CRM_STATUS } from "../lib/constants";
import { enumLabel } from "../lib/crmLabels";
import { SmartClock } from "../components/SmartClock";
import {
  Percent, DoorOpen, Users, CalendarClock, CheckCircle2, PhoneCall,
  Flame, CalendarDays, ArrowRight, TrendingUp, Handshake, Inbox, BedDouble, Sparkles,
} from "lucide-react";

// Compte à rebours animé de 0 → valeur cible
function useCountUp(target, duration = 900) {
  const [val, setVal] = useState(0);
  const ref = useRef(0);
  useEffect(() => {
    const from = ref.current;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = from + (target - from) * eased;
      setVal(current);
      if (p < 1) raf = requestAnimationFrame(tick);
      else ref.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

function KpiNumber({ value, suffix = "", className }) {
  const n = useCountUp(Number(value) || 0);
  const isZero = (Number(value) || 0) === 0;
  return (
    <span className={className} style={{ opacity: isZero ? 0.45 : 1 }}>
      {Math.round(n)}{suffix}
    </span>
  );
}

function Kpi({ icon: Icon, label, value, sub, cfg, suffix = "", tid, wide, onClick }) {
  const isZero = (Number(value) || 0) === 0;
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
      onClick={onClick}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick?.(); }}
      className={`dash-card group p-5 sm:p-6 flex flex-col justify-between min-h-[150px] cursor-pointer ${wide ? "sm:col-span-2" : ""}`}
      data-testid={tid}
    >
      <span className="dash-glow -right-6 -top-8 h-28 w-28" style={{ background: `radial-gradient(circle, ${cfg.blob}, transparent 70%)` }} />
      <span className="absolute inset-x-0 top-0 h-[3px] rounded-t-[24px]" style={{ background: `linear-gradient(90deg, ${cfg.a}, ${cfg.b})` }} />
      <div className="relative flex items-start justify-between">
        <span className="dash-ico h-12 w-12 rounded-2xl flex items-center justify-center text-white shrink-0"
          style={{ background: `linear-gradient(135deg, ${cfg.a}, ${cfg.b})`, boxShadow: `0 12px 26px -10px ${cfg.b}` }}>
          <Icon className="h-6 w-6" strokeWidth={1.75} />
        </span>
        <div className="text-right">
          <KpiNumber value={value} suffix={suffix} className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter tabular-nums text-white leading-none" />
          {isZero && <span className="dash-dot ml-1 align-middle" style={{ background: cfg.b }} />}
        </div>
      </div>
      <div className="relative mt-4">
        <p className="text-[13px] font-semibold text-foreground/90">{label}</p>
        {sub != null && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
      </div>
    </motion.div>
  );
}

function OccupancyHero({ rate, occupied, total, free, reserved, maintenance, onClick }) {
  const { t } = useTranslation();
  const animated = useCountUp(Number(rate) || 0, 1100);
  const R = 52, C = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(100, Number(rate) || 0));
  const offset = C * (1 - animated / 100);
  const isZero = pct === 0;
  return (
    <motion.div
      variants={{ hidden: { opacity: 0, y: 22 }, show: { opacity: 1, y: 0 } }}
      onClick={onClick}
      role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onClick?.(); }}
      className="dash-card group p-6 sm:p-7 flex flex-col sm:col-span-2 lg:row-span-2 min-h-[150px] lg:min-h-full cursor-pointer"
      data-testid="kpi-taux-occupation"
    >
      <span className="dash-glow -left-10 -bottom-10 h-48 w-48" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.55), transparent 70%)" }} />
      <span className="dash-glow -right-8 -top-10 h-36 w-36" style={{ background: "radial-gradient(circle, rgba(20,184,166,0.4), transparent 70%)" }} />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <span className="dash-ico h-11 w-11 rounded-2xl flex items-center justify-center text-white" style={{ background: "linear-gradient(135deg,#34d399,#0d9488)", boxShadow: "0 12px 26px -10px #14b8a6" }}>
            <Percent className="h-5 w-5" strokeWidth={1.75} />
          </span>
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-emerald-300/90">{t("crm.kpi.occupancy")}</p>
            <p className="text-xs text-muted-foreground">{t("crm.kpi.beds", { occ: occupied ?? 0, total: total ?? 0 })}</p>
          </div>
        </div>
        <ArrowRight className="h-5 w-5 text-emerald-300/80 group-hover:translate-x-1 transition-transform" />
      </div>

      <div className="relative flex-1 flex items-center justify-center py-5">
        <div className="relative h-[168px] w-[168px]">
          <svg viewBox="0 0 130 130" className="h-full w-full -rotate-90">
            <defs>
              <linearGradient id="occGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#34d399" />
                <stop offset="100%" stopColor="#0d9488" />
              </linearGradient>
            </defs>
            <circle cx="65" cy="65" r={R} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="11" />
            <circle cx="65" cy="65" r={R} fill="none" stroke="url(#occGrad)" strokeWidth="11" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={offset} style={{ filter: "drop-shadow(0 0 8px rgba(16,185,129,0.6))" }} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-display text-5xl font-extrabold tracking-tighter tabular-nums text-white" style={{ opacity: isZero ? 0.5 : 1 }}>{Math.round(animated)}<span className="text-2xl align-top">%</span></span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mt-1">{t("crm.kpi.occupancy")}</span>
          </div>
        </div>
      </div>

      <div className="relative grid grid-cols-3 gap-2">
        {[
          { v: free ?? 0, l: t("crm.kpi.freeRooms"), c: "#38bdf8" },
          { v: reserved ?? 0, l: t("crm.roomStatus.reservee", { defaultValue: "Réservées" }), c: "#a78bfa" },
          { v: maintenance ?? 0, l: t("crm.kpi.maintenance", { defaultValue: "Maintenance" }), c: "#fb923c" },
        ].map((s, i) => (
          <div key={i} className="rounded-xl bg-white/[0.03] border border-white/5 px-2.5 py-2 text-center">
            <p className="font-display text-lg font-bold tabular-nums" style={{ color: s.c }}>{s.v}</p>
            <p className="text-[10px] text-muted-foreground truncate">{s.l}</p>
          </div>
        ))}
      </div>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const goTab = (tab) => navigate(`/suivi-commercial?tab=${tab}`);
  const [dash, setDash] = useState({});
  const [visits, setVisits] = useState([]);
  const [events, setEvents] = useState([]);

  const load = async () => {
    const [d, v, e] = await Promise.all([
      api.get("/crm/dashboard"),
      api.get("/crm/visits").catch(() => ({ data: [] })),
      api.get("/events").catch(() => ({ data: [] })),
    ]);
    setDash(d.data || {}); setVisits(v.data || []); setEvents(e.data || []);
  };
  useEffect(() => { load(); }, []);

  const lang = i18n.language || "fr";
  const locale = lang.startsWith("nl") ? "nl-BE" : lang.startsWith("en") ? "en-GB" : "fr-BE";
  const todayLabel = new Date().toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const gh = new Date().getHours();
  const greeting = gh < 6 ? t("dash.greetNight") : gh < 12 ? t("dash.greetMorning") : gh < 18 ? t("dash.greetAfternoon") : gh < 22 ? t("dash.greetEvening") : t("dash.greetNight");
  const todayISO = new Date().toISOString().slice(0, 10);
  const todayVisits = visits.filter((v) => v.visit_date === todayISO && v.status === "planifiee").sort((a, b) => (a.visit_time || "").localeCompare(b.visit_time || ""));
  const todayEvents = events.filter((e) => e.date === todayISO).sort((a, b) => (a.time || "").localeCompare(b.time || ""));

  const d = dash || {};
  const maxPipe = Math.max(1, ...Object.values(d.pipeline || { x: 1 }));

  const KPIS = [
    { key: "chambres", icon: DoorOpen, label: t("crm.kpi.freeRooms"), value: d.rooms_free ?? 0, sub: t("crm.kpi.reserved", { n: d.rooms_reserved ?? 0 }), tid: "kpi-chambres-libres", tab: "occupation", cfg: { a: "#60a5fa", b: "#4f46e5", blob: "rgba(79,70,229,0.5)" } },
    { key: "prospects", icon: Users, label: t("crm.kpi.activeProspects"), value: d.prospects_active ?? 0, sub: t("crm.kpi.thisMonth", { n: d.new_month ?? 0 }), tid: "kpi-prospects-actifs", tab: "prospects", cfg: { a: "#fbbf24", b: "#f97316", blob: "rgba(249,115,22,0.5)" } },
    { key: "visites", icon: CalendarClock, label: t("crm.kpi.plannedVisits"), value: d.visits_planned ?? 0, sub: t("crm.kpi.doneVisits", { n: d.visits_done ?? 0 }), tid: "kpi-visites-planifiees", tab: "visites", cfg: { a: "#e879f9", b: "#7c3aed", blob: "rgba(124,58,237,0.5)" } },
    { key: "admissions", icon: CheckCircle2, label: t("crm.kpi.admissions"), value: d.admissions ?? 0, sub: t("crm.kpi.conv", { n: d.conversion ?? 0 }), tid: "kpi-admissions", tab: "admissions", cfg: { a: "#fb7185", b: "#db2777", blob: "rgba(219,39,119,0.5)" } },
    { key: "relances", icon: PhoneCall, label: t("crm.kpi.urgentFollowups"), value: d.to_followup ?? 0, sub: t("crm.kpi.hotProspects", { n: d.hot ?? 0 }), tid: "kpi-relances-urgentes", tab: "relances", cfg: { a: "#f87171", b: "#e11d48", blob: "rgba(225,29,72,0.5)" } },
    { key: "partenaires", icon: Handshake, label: t("crm.kpi.activePartners"), value: d.active_partners ?? 0, sub: t("crm.kpi.partnerPortal"), tid: "kpi-partenaires-actifs", tab: "espace", cfg: { a: "#22d3ee", b: "#2563eb", blob: "rgba(37,99,235,0.5)" } },
    { key: "demandes", icon: Inbox, label: t("crm.kpi.requestsWeek"), value: d.requests_week ?? 0, sub: t("crm.kpi.requestsNew", { n: d.requests_new ?? 0 }), tid: "kpi-demandes", tab: "espace", wide: true, cfg: { a: "#a3e635", b: "#10b981", blob: "rgba(16,185,129,0.5)" } },
  ];

  return (
    <div className="p-5 sm:p-8 max-w-[1440px] mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="flex-1 min-w-0" data-tour="dash-header">
          <p className="text-xs uppercase tracking-[0.28em] font-bold text-emerald-300 capitalize flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> {todayLabel}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter mt-2" data-testid="greeting-text">
            <span className="text-white">{greeting} </span>
            <span style={{ background: "linear-gradient(120deg,#34d399,#22d3ee,#a78bfa)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              {user?.name?.split(" ").slice(-1)[0]}
            </span>{" "}
            <span className="inline-block" style={{ animation: "floaty 3s ease-in-out infinite" }}>👋</span>
          </h1>
          <p className="text-muted-foreground mt-2 inline-flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-emerald-400" /> {t("crm.subtitle")}</p>
        </motion.div>
        <SmartClock className="no-print w-full lg:w-[380px] shrink-0" />
      </div>

      {/* KPIs — bento */}
      <motion.div
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
        initial="hidden" animate="show"
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8"
        data-tour="dash-kpi"
      >
        <OccupancyHero rate={d.occupancy_rate ?? 0} occupied={d.beds_occupied} total={d.beds_total} free={d.rooms_free} reserved={d.rooms_reserved} maintenance={d.rooms_maintenance} onClick={() => goTab("occupation")} />
        {KPIS.map((k) => (
          <Kpi key={k.key} icon={k.icon} label={k.label} value={k.value} sub={k.sub} cfg={k.cfg} tid={k.tid} wide={k.wide} onClick={() => goTab(k.tab)} />
        ))}
      </motion.div>

      {/* Panneaux inférieurs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        {/* Pipeline */}
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="dash-card lg:col-span-2 p-6" data-testid="pipeline-commercial-panel">
          <span className="dash-glow -right-10 -top-10 h-40 w-40" style={{ background: "radial-gradient(circle, rgba(249,115,22,0.28), transparent 70%)" }} />
          <div className="relative flex items-center justify-between mb-5">
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><Flame className="h-5 w-5 text-orange-400" /> {t("crm.pipeline")}</h3>
            <Link to="/suivi-commercial" className="text-xs text-emerald-400 hover:text-emerald-300 transition-colors flex items-center gap-1 font-semibold" data-testid="dash-goto-crm">{t("nav.crm")} <ArrowRight className="h-3 w-3" /></Link>
          </div>
          <div className="relative space-y-2.5">
            {Object.keys(CRM_STATUS).map((k, i) => {
              const v = CRM_STATUS[k];
              const n = d.pipeline?.[k] || 0;
              const pct = (n / maxPipe) * 100;
              return (
                <button key={k} type="button" onClick={() => navigate(`/suivi-commercial?tab=prospects&status=${k}`)}
                  className="w-full flex items-center gap-3 group/row rounded-lg px-1.5 py-0.5 -mx-1.5 hover:bg-white/[0.04] transition-colors text-left cursor-pointer"
                  data-testid={`pipeline-bar-${k}`} title={t("crm.pipelineFilterHint", { defaultValue: "Filtrer les prospects sur ce statut" })}>
                  <div className="w-44 shrink-0 text-[13px] text-foreground/75 group-hover/row:text-foreground transition-colors">{enumLabel(t, "status", k, CRM_STATUS)}</div>
                  <div className="flex-1 h-3 rounded-full bg-white/[0.03] border border-white/5 overflow-hidden">
                    <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ delay: 0.2 + i * 0.03, duration: 0.9, ease: "easeOut" }}
                      className="h-full rounded-full" style={{ background: `linear-gradient(90deg, ${v.text}99, ${v.text})`, boxShadow: n > 0 ? `0 0 10px ${v.text}66` : "none" }} />
                  </div>
                  <div className="w-8 text-right text-sm font-bold tabular-nums" style={{ opacity: n === 0 ? 0.4 : 1 }}>{n}</div>
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* Aujourd'hui : visites + agenda */}
        <motion.div initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
          className="dash-card p-6" data-testid="agenda-panel">
          <span className="dash-glow -left-10 -bottom-8 h-40 w-40" style={{ background: "radial-gradient(circle, rgba(16,185,129,0.24), transparent 70%)" }} />
          <div className="relative flex items-center justify-between">
            <h3 className="font-display font-bold flex items-center gap-2"><CalendarClock className="h-5 w-5 text-emerald-400" /> {t("crm.visit.todayTitle")}</h3>
            <Link to="/suivi-commercial" className="text-emerald-400 hover:text-emerald-300 transition-colors"><ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div className="relative mt-4 space-y-2" data-testid="dash-today-visits">
            {todayVisits.length === 0 && (
              <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-6 text-center">
                <BedDouble className="h-7 w-7 mx-auto text-emerald-400/50 mb-2" />
                <p className="text-sm text-muted-foreground">{t("crm.visit.noneToday")}</p>
              </div>
            )}
            {todayVisits.map((v) => (
              <div key={v.id} className="flex items-center gap-3 text-sm rounded-xl px-3 py-2 bg-white/[0.02] border border-white/5 hover:border-emerald-500/30 transition-colors">
                <span className="font-mono text-xs text-emerald-300 w-11 shrink-0 tabular-nums">{v.visit_time || "--:--"}</span>
                <span className="truncate">{v.prospect_name || "—"}</span>
              </div>
            ))}
          </div>
          <div className="relative mt-5 pt-4 border-t border-white/10">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-blue-300 flex items-center gap-1.5"><CalendarDays className="h-4 w-4" /> {t("dash.today")}</span>
              {user?.role !== "ceo" && <Link to="/agenda" className="text-xs text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 font-semibold">{t("nav.agenda")} <ArrowRight className="h-3 w-3" /></Link>}
            </div>
            <div className="mt-3 space-y-2">
              {todayEvents.length === 0 && (
                <div className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] px-4 py-5 text-center">
                  <CalendarDays className="h-6 w-6 mx-auto text-blue-400/50 mb-2" />
                  <p className="text-sm text-muted-foreground">{t("dash.noEventToday")}</p>
                </div>
              )}
              {todayEvents.map((e) => (
                <div key={e.id} className="flex items-center gap-3 text-sm rounded-xl px-3 py-2 bg-white/[0.02] border border-white/5 hover:border-blue-500/30 transition-colors">
                  <span className="font-mono text-xs text-blue-300 w-11 shrink-0 tabular-nums">{e.time || "--:--"}</span>
                  <span className="truncate">{e.title}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
